import React, { useState, useEffect } from "react";
import { Sparkles, Info } from "lucide-react";
import { toast } from "sonner";
import L from "../../lib/leaflet";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

// "Commission a Video" — 2-step flow per Feb 2026 UX simplification.
//
// Step 1: explains what a commission is, asks where to film, optional
//          notes for the producer. The EP no longer titles the work —
//          the backend auto-generates a friendly title from the location
//          (the person paying shouldn't name another producer's video).
// Step 2: pricing, expiry, anonymity. Same as before.
const CommissionDialog = ({ open, onClose }) => {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [posting, setPosting] = useState(false);
  const [description, setDescription] = useState(""); // "Any additional requests"
  // 4-option chip selector for filming timeframe — prepended to the
  // creator-visible description so no backend schema change is needed.
  const [whenPref, setWhenPref] = useState("This week");
  const [point, setPoint] = useState({ lat: 48.8584, lng: 2.2945 });
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [label, setLabel] = useState("");
  const [radiusM, setRadiusM] = useState(1000);
  const [maxDuration, setMaxDuration] = useState(32);
  const [storageDays, setStorageDays] = useState(10);
  const [featured, setFeatured] = useState(false);
  const [bounty, setBounty] = useState(5);
  const [coverOverruns, setCoverOverruns] = useState(0);
  const [expiryHours, setExpiryHours] = useState(24);
  const [anonymous, setAnonymous] = useState(false);
  const [showInFeed, setShowInFeed] = useState(true);
  const mapDivRef = React.useRef(null);
  const leafletRef = React.useRef({ map: null, marker: null, circle: null });

  // Cost preview math (mirrors backend)
  const DURATION_COSTS = { 8: 0, 16: 1.0, 32: 2.5, 64: 6.0, 120: 10.0, 180: 14.0, 360: 24.0, 540: 32.0 };
  const STORAGE_COSTS = { 10: 0, 20: 1.0, 30: 2.0, 40: 3.0 };
  const POSTING_FEE = 0.25;
  const pubCost = 1.0 + (DURATION_COSTS[maxDuration] || 0) + (STORAGE_COSTS[storageDays] || 0) + (featured ? 5 : 0);
  const grandTotal = pubCost + Number(bounty || 0) + Number(coverOverruns || 0) + POSTING_FEE;

  // Reverse-geocode whenever the point changes
  useEffect(() => {
    let cancelled = false;
    const lookup = async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${point.lat}&lon=${point.lng}&format=json`);
        const data = await res.json();
        if (cancelled) return;
        const c = data.address?.city || data.address?.town || data.address?.village || data.address?.county || "";
        const co = data.address?.country || "";
        if (c) setCity(c);
        if (co) setCountry(co);
      } catch (err) { /* ignore */ }
    };
    lookup();
    return () => { cancelled = true; };
  }, [point.lat, point.lng]);

  // Initialise the Leaflet map on Step 1.
  //
  // iOS WKWebView race notes (Feb 2026 — user reported black/blank map):
  //  - Radix Dialog mounts via a portal; the ref callback fires AFTER
  //    React's first commit AND after Radix's open animation starts.
  //  - If we call L.map() while the container is still display:none or
  //    sized 0×0, Leaflet caches that size for the lifetime of the map.
  //    invalidateSize() helps but doesn't always recover on iOS.
  //  - We now wait until BOTH `open` AND `step === 1` are true AND the
  //    ref is actually attached AND the container has non-zero size,
  //    polling once per 100ms (max 1s) before giving up.
  useEffect(() => {
    if (!open || step !== 1 || leafletRef.current.map) return;
    let cancelled = false;
    let tries = 0;
    const tryInit = () => {
      if (cancelled || leafletRef.current.map) return;
      const el = mapDivRef.current;
      if (!el || el.clientWidth === 0 || el.clientHeight === 0) {
        if (++tries > 10) return; // give up after ~1 sec
        setTimeout(tryInit, 100);
        return;
      }
      const m = L.map(el, { zoomControl: true }).setView([point.lat, point.lng], 4);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap contributors © CARTO',
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(m);
      const marker = L.marker([point.lat, point.lng], { draggable: true }).addTo(m);
      const circle = L.circle([point.lat, point.lng], { radius: radiusM, color: '#f59e0b', fillOpacity: 0.15 }).addTo(m);
      marker.on('dragend', () => {
        const ll = marker.getLatLng();
        setPoint({ lat: ll.lat, lng: ll.lng });
        circle.setLatLng(ll);
      });
      m.on('click', (e) => {
        marker.setLatLng(e.latlng);
        circle.setLatLng(e.latlng);
        setPoint({ lat: e.latlng.lat, lng: e.latlng.lng });
      });
      leafletRef.current = { map: m, marker, circle };
      // Trigger size recompute AFTER paint — Leaflet sometimes caches
      // the wrong width on first init even when clientWidth > 0.
      setTimeout(() => { try { m.invalidateSize(); } catch (e) { /* noop */ } }, 50);
      setTimeout(() => { try { m.invalidateSize(); } catch (e) { /* noop */ } }, 400);
    };
    // Defer one frame so React paints the portal first.
    setTimeout(tryInit, 50);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  useEffect(() => {
    return () => {
      if (leafletRef.current.map) {
        leafletRef.current.map.remove();
        leafletRef.current = { map: null, marker: null, circle: null };
      }
    };
  }, []);

  useEffect(() => {
    if (leafletRef.current.circle) leafletRef.current.circle.setRadius(radiusM);
  }, [radiusM]);

  const handleSubmit = async () => {
    if (!city.trim() && !country.trim() && !label.trim()) {
      toast.error("Tell us where to film — drop a pin, or type a city/country.");
      setStep(1);
      return;
    }
    if (user.credits < grandTotal) {
      toast.error(`Need ${grandTotal.toFixed(2)} credits — you have ${user.credits.toFixed(2)}`);
      return;
    }
    setPosting(true);
    try {
      // Prefix the EP's notes with their preferred timeframe so the
      // creator who picks this up sees the urgency at a glance. Keeps
      // the API contract identical — no backend migration required.
      const fullDescription = description.trim()
        ? `[When: ${whenPref}] ${description.trim()}`
        : `[When: ${whenPref}]`;
      await api.post(`/commissions`, {
        // Title intentionally omitted — backend auto-generates from
        // location label/city/country. EPs no longer name another
        // producer's video.
        description: fullDescription,
        location: { lat: point.lat, lng: point.lng, city: city || null, country: country || null, label: label.trim() || null },
        radius_m: radiusM,
        max_duration_seconds: maxDuration,
        storage_days: storageDays,
        featured,
        bounty_credits: Number(bounty || 0),
        cover_overruns_up_to: Number(coverOverruns || 0),
        expiry_hours: expiryHours,
        anonymous,
        show_in_feed: showInFeed,
      });
      toast.success("Commission posted! A creator will see it on the map.");
      await refreshUser();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't post commission");
    }
    setPosting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-gray-900 border-gray-800 text-white w-[95vw] max-w-2xl overflow-y-auto p-4 sm:p-6
                   top-2 translate-y-0 sm:top-1/2 sm:translate-y-[-50%]"
        // On iOS the centered Radix dialog can push the title above the
        // notch; we pin the top to a fixed offset that respects the
        // safe-area-inset and cap the height to whatever's usable.
        style={{
          maxHeight: 'min(92dvh, calc(100dvh - env(safe-area-inset-top, 0px) - 1rem))',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
          WebkitOverflowScrolling: 'touch',
        }}
        // Stop the auto-focus pop-keyboard behavior — there's a map +
        // chip selectors above the first input, so the keyboard would
        // otherwise hide most of the form.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Surface an explicit close button at top-right (Radix renders
        // one by default but we relabel + bump z-index so it doesn't
        // get covered by the Leaflet map inside step 1).
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Sparkles className="h-5 w-5 text-amber-400" /> Commission a Video
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">
            A commission is a paid request for another GlobalEyes producer to film
            something specific that you’d like to see on TV. Your credits stay parked safely until someone
            delivers — refunded if no one shows up in time.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator — only 2 dots now */}
        <div className="flex gap-1.5 my-2">
          {[1, 2].map((s) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${s <= step ? 'bg-amber-400' : 'bg-gray-800'}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3" data-testid="commission-step-1">
            <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-3 text-xs text-amber-200/90 flex gap-2">
              <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
              <p>
                You won't title or shoot the video — that's the producer's craft.
                We'll auto-name your commission from the location below so creators
                in the area can find it.
              </p>
            </div>

            <p className="text-gray-300 text-sm font-semibold">Where or what do you want filmed?</p>
            <div ref={mapDivRef} className="w-full h-56 sm:h-64 rounded-lg overflow-hidden bg-gray-800" data-testid="commission-map" />
            <p className="text-gray-500 text-[11px]">Tap the map to drop a pin — or type below.</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-400">City</Label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} className="bg-gray-800 border-gray-700 text-white text-sm" placeholder="Paris" data-testid="commission-city-input" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Country</Label>
                <Input value={country} onChange={(e) => setCountry(e.target.value)} className="bg-gray-800 border-gray-700 text-white text-sm" placeholder="France" data-testid="commission-country-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-400">What's at this spot? (optional)</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} className="bg-gray-800 border-gray-700 text-white text-sm" placeholder="Eiffel Tower" data-testid="commission-label-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Filming radius: <span className="text-amber-400 font-semibold">{radiusM >= 1000 ? `${radiusM/1000} km` : `${radiusM} m`}</span></Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1">
                {[250, 1000, 5000, 25000].map((r) => (
                  <button key={r} onClick={() => setRadiusM(r)} className={`px-2 py-1.5 rounded text-xs ${radiusM === r ? 'bg-amber-500 text-gray-900 font-semibold' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid={`radius-${r}`}>
                    {r >= 1000 ? `${r/1000} km` : `${r} m`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-400">When do you want it filmed?</Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1">
                {["ASAP", "This week", "This weekend", "Any time"].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setWhenPref(opt)}
                    className={`px-2 py-1.5 rounded text-xs ${whenPref === opt ? 'bg-amber-500 text-gray-900 font-semibold' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                    data-testid={`commission-when-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-400">Any additional requests for whoever takes this commission? (optional)</Label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} rows={3} className="w-full bg-gray-800 border border-gray-700 rounded text-white text-sm p-2 mt-1" placeholder="e.g. golden hour, no people in shot, walk past slowly..." data-testid="commission-description-input" />
              <p className="text-gray-500 text-[10px] mt-1">{description.length}/500 characters</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button onClick={() => setStep(2)} className="bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold" data-testid="commission-next-1">Next →</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="commission-step-2">
            <p className="text-gray-300 text-sm font-semibold">Pricing & treats 🎁</p>
            <p className="text-gray-500 text-xs">A friendly tip on top of covering the publishing cost makes your offer stand out.</p>

            <div>
              <Label className="text-xs text-gray-400">Max video length</Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-1">
                {[8, 16, 32, 64, 120, 180, 360, 540].map((s) => (
                  <button key={s} onClick={() => setMaxDuration(s)} className={`px-2 py-1.5 rounded text-xs ${maxDuration === s ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid={`commission-dur-${s}`}>
                    {s >= 120 ? `${s/60} min` : `${s}s`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-400">Keep it online for</Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1">
                {[10, 20, 30, 40].map((d) => (
                  <button key={d} onClick={() => setStorageDays(d)} className={`px-2 py-1.5 rounded text-xs ${storageDays === d ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>{d} days</button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} /> Feature this video (+5 credits)
            </label>

            <div>
              <Label className="text-xs text-gray-400">Tip / bounty: <span className="text-amber-400 font-semibold">{Number(bounty || 0).toFixed(2)} credits</span></Label>
              <input type="range" min={0} max={50} step={1} value={bounty} onChange={(e) => setBounty(Number(e.target.value))} className="w-full accent-amber-400" data-testid="commission-bounty-slider" />
            </div>

            <div>
              <Label className="text-xs text-gray-400">Cushion for going-the-extra-mile (optional)</Label>
              <input type="range" min={0} max={20} step={1} value={coverOverruns} onChange={(e) => setCoverOverruns(Number(e.target.value))} className="w-full accent-emerald-400" />
              <p className="text-gray-500 text-[10px]">If a creator shoots a longer or featured version, we'll cover up to {Number(coverOverruns || 0).toFixed(2)} extra credits.</p>
            </div>

            <div>
              <Label className="text-xs text-gray-400">Offer expires in</Label>
              <div className="grid grid-cols-5 gap-1.5 mt-1">
                {[{ v: 1, label: "1h" }, { v: 6, label: "6h" }, { v: 24, label: "24h" }, { v: 72, label: "3 days" }, { v: 168, label: "7 days" }].map((o) => (
                  <button key={o.v} onClick={() => setExpiryHours(o.v)} className={`px-2 py-1.5 rounded text-xs ${expiryHours === o.v ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>{o.label}</button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="commission-anonymous" /> Stay anonymous (your name & affiliate links won't show)
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" checked={showInFeed} onChange={(e) => setShowInFeed(e.target.checked)} /> Show the finished video in my profile feed
            </label>

            {/* Cost summary */}
            <div className="bg-gray-800 rounded-lg p-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">Publishing cost</span><span className="text-cyan-400">{pubCost.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Tip for the creator</span><span className="text-amber-400">{Number(bounty || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Cushion</span><span className="text-emerald-400">{Number(coverOverruns || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Bulletin-board fee (kept)</span><span className="text-gray-300">{POSTING_FEE.toFixed(2)}</span></div>
              <div className="flex justify-between mt-2 pt-2 border-t border-gray-700 font-semibold"><span className="text-white">Locks today</span><span className="text-white">{grandTotal.toFixed(2)} credits</span></div>
              <p className="text-gray-500 text-[10px] mt-1">Refunded {(grandTotal - POSTING_FEE).toFixed(2)} if no one accepts in time.</p>
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-2 pt-2">
              <Button onClick={() => setStep(1)} variant="outline" className="border-gray-700 text-gray-300 w-full sm:w-auto">← Back</Button>
              <Button onClick={handleSubmit} disabled={posting} className="bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold w-full sm:w-auto" data-testid="commission-submit">
                {posting ? "Posting..." : `Post Commission (${grandTotal.toFixed(2)})`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CommissionDialog;
